import type { Namespace } from '../types';

// Transfer orders (R-144): the movement document + reservation ledger. Receiving
// drives the asset lifecycle move through the same gates as a direct transition.
export const transferOrders = {
  title: { de: 'Transferaufträge', en: 'Transfer orders' },
  desc: {
    de: 'Bewegungsbelege für Assets — Deploy, Tausch, Reparaturrückgabe und Umzug. Der Empfang treibt den Lebenszyklus.',
    en: 'Asset movement documents — deploy, swap, return-to-repair and relocation. Receiving drives the lifecycle move.',
  },
  navLabel: { de: 'Transfers', en: 'Transfers' },

  colNumber: { de: 'Nummer', en: 'Number' },
  colPurpose: { de: 'Zweck', en: 'Purpose' },
  colStatus: { de: 'Status', en: 'Status' },
  colRoute: { de: 'Von → Nach', en: 'From → To' },
  colCreated: { de: 'Erstellt', en: 'Created' },
  colActions: { de: 'Aktionen', en: 'Actions' },
  colLines: { de: 'Positionen', en: 'Lines' },

  purpose_deploy: { de: 'Deploy', en: 'Deploy' },
  purpose_swap_prep: { de: 'Tausch-Vorbereitung', en: 'Swap prep' },
  purpose_return_repair: { de: 'Reparaturrückgabe', en: 'Return to repair' },
  purpose_transfer: { de: 'Umzug', en: 'Transfer' },

  status_draft: { de: 'Entwurf', en: 'Draft' },
  status_in_transit: { de: 'Unterwegs', en: 'In transit' },
  status_received: { de: 'Empfangen', en: 'Received' },
  status_cancelled: { de: 'Storniert', en: 'Cancelled' },

  stage_requested: { de: 'Angefordert', en: 'Requested' },
  stage_picked: { de: 'Kommissioniert', en: 'Picked' },
  stage_staged: { de: 'Bereitgestellt', en: 'Staged' },
  stage_delivered: { de: 'Zugestellt', en: 'Delivered' },

  filterAll: { de: 'Alle Status', en: 'All statuses' },

  create: { de: 'Transfer anlegen', en: 'New Transfer' },
  createTitle: { de: 'Transferauftrag anlegen', en: 'New transfer order' },
  createSubmit: { de: 'Anlegen', en: 'Create' },
  cancel: { de: 'Abbrechen', en: 'Cancel' },
  fieldPurpose: { de: 'Zweck', en: 'Purpose' },
  fieldFromLocation: { de: 'Von Standort', en: 'From location' },
  fieldToLocation: { de: 'Nach Standort', en: 'To location' },
  fieldToUser: { de: 'An Person (optional)', en: 'To person (optional)' },
  fieldNote: { de: 'Notiz', en: 'Note' },
  fieldAssets: { de: 'Assets (aus Lager)', en: 'Assets (from stock)' },
  selectLocation: { de: '— Standort wählen —', en: '— select location —' },
  selectUser: { de: '— Person wählen —', en: '— select person —' },
  selectAsset: { de: 'Asset hinzufügen…', en: 'Add an asset…' },
  phNote: {
    de: 'z. B. Anlass, Übergabehinweise oder Ansprechperson',
    en: 'e.g. the reason, hand-over notes or a contact',
  },
  noAssets: { de: 'Noch keine Assets gewählt.', en: 'No assets selected yet.' },
  removeAsset: { de: 'Asset entfernen', en: 'Remove asset' },

  detailTitle: { de: 'Transferauftrag {number}', en: 'Transfer order {number}' },
  linesTitle: { de: 'Positionen', en: 'Lines' },
  eventsTitle: { de: 'Verlauf', en: 'History' },
  advanceStage: { de: 'Stufe: {stage}', en: 'Mark {stage}' },
  statusControl: { de: 'Status', en: 'Status' },
  dispatch: { de: 'Versenden', en: 'Dispatch' },
  receive: { de: 'Empfangen', en: 'Receive' },
  cancelOrder: { de: 'Stornieren', en: 'Cancel order' },
  open: { de: 'Öffnen', en: 'Open' },

  emptyFilteredMsg: {
    de: 'Kein Transferauftrag hat diesen Status. Das Feld leeren, um alle zu sehen.',
    en: 'No transfer order has this status. Clear the filter to see them all.',
  },
  confirmDispatchTitle: { de: '{number} versenden?', en: 'Dispatch {number}?' },
  confirmDispatchMsg: {
    de: 'Der Auftrag verlässt das Lager; seine Positionen sind bis zum Empfang unterwegs.',
    en: 'The order leaves the store, and its lines are in transit until it is received.',
  },
  confirmReceiveTitle: { de: '{number} empfangen?', en: 'Receive {number}?' },
  confirmReceiveMsg: {
    de: 'Der Empfang bewegt jede Position über die Lebenszyklus-Prüfungen an ihr Ziel. Das lässt sich nicht rückgängig machen.',
    en: 'Receiving moves every line to its destination through the asset lifecycle gates. This cannot be undone.',
  },
  confirmCancelTitle: { de: '{number} stornieren?', en: 'Cancel {number}?' },
  confirmCancelMsg: {
    de: 'Die Reservierung jeder Position wird freigegeben und der Auftrag geschlossen. Das lässt sich nicht rückgängig machen.',
    en: 'The reservation on every line is released and the order is closed. This cannot be undone.',
  },
  toastCreated: { de: '{number} angelegt', en: '{number} created' },
  toastDispatch: { de: '{number} versendet', en: '{number} dispatched' },
  toastReceive: { de: '{number} empfangen', en: '{number} received' },
  toastCancel: { de: '{number} storniert', en: '{number} cancelled' },
  toastStage: { de: '{asset}: {stage}', en: '{asset} marked {stage}' },
  emptyTitle: { de: 'Keine Transferaufträge', en: 'No transfer orders' },
  emptyMsg: {
    de: 'Legen Sie einen Transfer an, um Assets zu bewegen.',
    en: 'Create a transfer to move assets.',
  },
  loadError: { de: 'Transferaufträge konnten nicht geladen werden', en: 'Failed to load transfer orders' },
} satisfies Namespace;
